import { find } from 'lodash'
import React, { FC, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { ReactSVG } from 'react-svg'
import { RootState } from '../../../../../states/containers/store'
import { ResponseMonthlyTeamReport, ResponseMonthlyMemberReport } from '../../../../../states/types/report'
import { buildWorkTime, nowDate, secToHour } from '../../../../../utils/date'
import CATEGORY from '../../../../constants/category'
import Loading from '../../../common/Loading'

interface List {
  id: number
  title: string
  fill: string
  bg_fill: string
  iconPath: string
  past: {
    time: number
    biz: number
  }
  current: {
    time: number
    biz: number
  }
}

interface Label {
  id: number
  title: string
  fill: string
}

interface GraphList {
  title: string
  value: number
  fill: string
  bg_fill: string
}

interface Search {
  startDate: string
  endDate: string
  teamIds: number[]
  teamNames: string[]
  memberIds: number[]
  memberNames: string[]
  keywords: string[]
  serviceNames: string[]
  categoryIds: number[]
  categoryNames: string[]
  efficiencyLevel: string[]
  stressLevel: string[]
  focusLevel: string[]
  sortKey?: string,
  sortType?: string,
}

interface Props {
  target: string
  searchModalOpenCallback: (search: Search) => void,
}

const MonthlyCategory: FC<Props> = (props) => {
  const { t } = useTranslation()

  const {
    target,
    searchModalOpenCallback,
  } = props

  const {
    report,
  } = useSelector((state: RootState) => (state))

  const tipCurrentRef = useRef<HTMLDivElement>(null)
  const tipPastRef = useRef<HTMLDivElement>(null)

  const [isDisplayCurrentTip, setIsDisplayCurrentTip] = useState<boolean>(false)
  const [isDisplayPastTip, setIsDisplayPastTip] = useState<boolean>(false)

  const [categoryList, setCategoryList] = useState<List[]>([])

  const [timeMaxValue, setTimeMaxValue] = useState<number>(0)
  const [bizMaxValue, setBizMaxValue] = useState<number>(0)

  const [isDisplayTime, setIsDisplayTime] = useState<boolean>(true)

  const [focusTitle, setFocusTitle] = useState<string>("")

  const [reportData, setReportData] = useState<ResponseMonthlyTeamReport | ResponseMonthlyMemberReport>()

  const [isDisplayComment, setIsDisplayComment] = useState<boolean>(true)

  const [labelList, setLabelList] = useState<Label[]>([])

  const brightness = (rgb: string) => (
    (parseInt(rgb.substring(1, 3), 16) * 0.299)
    + (parseInt(rgb.substring(3, 5), 16) * 0.587)
    + (parseInt(rgb.substring(5, 7), 16) * 0.114)
  )

  const displayTip = (data: GraphList, angle: number, sum: number, type?: string) => {
    if (!tipCurrentRef.current || !tipPastRef.current) return
    if (type === "current") {
      tipCurrentRef.current.style.left = `${200 + (150 * Math.cos((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
      tipCurrentRef.current.style.top = `${200 + (150 * Math.sin((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
      tipCurrentRef.current.style.backgroundColor = `${data.fill}`
      tipCurrentRef.current.style.color = brightness(data.fill) >= 140 ? '#000000' : '#FFFFFF'
      if (isDisplayTime) {
        tipCurrentRef.current.textContent = `${data.title} : ${buildWorkTime(data.value || 0)}`
      } else {
        tipCurrentRef.current.textContent = `${data.title} : ${data.value.toLocaleString()} Biz`
      }
      setIsDisplayCurrentTip(true)
    } else {
      tipPastRef.current.style.left = `${200 + (120 * Math.cos((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
      tipPastRef.current.style.top = `${150 + (120 * Math.sin((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
      tipPastRef.current.style.backgroundColor = `${data.fill}`
      tipPastRef.current.style.color = brightness(data.fill) >= 140 ? '#000000' : '#FFFFFF'
      if (isDisplayTime) {
        tipPastRef.current.textContent = `${data.title} : ${buildWorkTime(data.value || 0)}`
      } else {
        tipPastRef.current.textContent = `${data.title} : ${data.value.toLocaleString()} Biz`
      }
      setIsDisplayPastTip(true)
    }
  }

  const hiddenTip = () => {
    setIsDisplayCurrentTip(false)
    setIsDisplayPastTip(false)
  }

  const closeScreen = () => {
    hiddenTip()
    setFocusTitle("")
  }

  const d = (cx: number, cy: number, r: number, a: number, s: number) => {
    const rad1 = ((s - 90) / 180) * Math.PI
    const rad2 = ((a + s - 90) / 180) * Math.PI
    const params = [
      cx + r * Math.cos(rad1), cy + r * Math.sin(rad1),
      r, r,
      a > 180 ? 1 : 0,
      cx + r * Math.cos(rad2), cy + r * Math.sin(rad2),
      cx, cy,
    ]

    return params.reduce((str, param) => str.replace('%d', String(param)), 'M %d,%d A %d,%d 0 %d,1 %d,%d L %d,%d Z')
  }

  const clickArcArea = (title: string) => {
    if (focusTitle === title) {
      setFocusTitle("")
    } else {
      setFocusTitle(title)
    }
  }

  const arc = (svg: HTMLElement | null, cx: number, cy: number, r: number, list: GraphList[], type?: string, focus?: string) => {
    if (!svg) return
    svg.innerHTML = ""
    let total = 0
    let s = 0

    if (!list.length) {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      const a = 359.999

      path.setAttribute('d', d(cx, cy, r, a, s))
      path.style.opacity = "1"
      path.setAttribute('fill', "#ECF0F5")
      path.setAttribute('stroke', '#fff')
      svg.appendChild(path)
      return
    }

    list.forEach((data: GraphList) => {
      total += data.value
    })
    list.forEach((data: GraphList) => {
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
      const a = (data.value / total) * 360
      const sum = s

      if (data.title === focus) {
        path.setAttribute('d', d(cx, cy, r * 1.05, a, s))
        path.style.opacity = "1"
      } else {
        path.setAttribute('d', d(cx, cy, r, a, s))
        if (focus) {
          path.style.opacity = "0.2"
        } else {
          path.style.opacity = "1"
        }
      }
      path.setAttribute('fill', data.fill)
      path.setAttribute('stroke', '#fff')
      path.onclick = () => clickArcArea(data.title)
      path.onmouseover = () => displayTip(data, a, sum, type)
      path.onmouseout = () => hiddenTip()
      path.style.cursor = "pointer"
      svg.appendChild(path)
      s += a
    })
  }

  const outerLabel = (wrap: HTMLElement | null, list: GraphList[], type: string, focus?: string) => {
    if (!wrap) return
    wrap.innerHTML = ""
    let total = 0
    let sum = 0

    list.forEach((data: GraphList) => {
      total += data.value
    })
    list.forEach((data: GraphList) => {
      const div = document.createElement('div')
      const span = document.createElement('span')
      const percentage = (data.value / total) * 100
      if (percentage < 3) return
      const angle = percentage * 3.6
      const focusAdd = data.title === focus ? 10 : 0
      const fontSize = data.title === focus ? 24 : 18

      span.textContent = `${Math.trunc(percentage)}`
      span.style.fontSize = `${fontSize}px`

      div.style.position = "absolute"
      div.style.fontSize = "10px"
      div.style.color = "#6E6D6D"
      div.style.transform = "translateX(-50%) translateY(-50%)"
      if (type === "current") {
        div.style.top = `${200 + ((170 + focusAdd) * Math.sin((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
        div.style.left = `${200 + ((170 + focusAdd) * Math.cos((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
      } else {
        div.style.top = `${150 + ((140 + focusAdd) * Math.sin((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
        div.style.left = `${200 + ((140 + focusAdd) * Math.cos((sum + (angle / 2) - 90) * (Math.PI / 180)))}px`
      }
      div.innerHTML = `${span.outerHTML}%`

      wrap.appendChild(div)
      sum += angle
    })
  }

  const switchDisplay = (displayTime: boolean) => {
    setFocusTitle("")
    setIsDisplayTime(displayTime)
  }

  useEffect(() => {
    let pastList:GraphList[] = []
    let currentList:GraphList[] = []
    let tmpPastSortList:{id: number, value: number}[] = []
    let tmpCurrentSortList:{id: number, value: number}[] = []
    if (isDisplayTime) {
      tmpPastSortList = reportData?.category_work_comparisons?.past.time.data || []
      pastList = tmpPastSortList.map((value) => {
        const list = find(CATEGORY, { id: value.id })
        return {
          title: t(`report.detail.category_name.${list?.code}`),
          value: value.value,
          fill: list?.color || "#333",
          bg_fill: list?.bg_color || "#CCC",
        }
      })
      tmpCurrentSortList = reportData?.category_work_comparisons?.current.time.data || []
      currentList = tmpCurrentSortList.map((value) => {
        const list = find(CATEGORY, { id: value.id })
        return {
          title: t(`report.detail.category_name.${list?.code}`),
          value: value.value,
          fill: list?.color || "#333",
          bg_fill: list?.bg_color || "#CCC",
        }
      })
    } else {
      tmpPastSortList = reportData?.category_work_comparisons?.past.biz.data || []
      pastList = tmpPastSortList.map((value) => {
        const list = find(CATEGORY, { id: value.id })
        return {
          title: t(`report.detail.category_name.${list?.code}`),
          value: value.value,
          fill: list?.color || "#333",
          bg_fill: list?.bg_color || "#CCC",
        }
      })
      tmpCurrentSortList = reportData?.category_work_comparisons?.current.biz.data || []
      currentList = tmpCurrentSortList.map((value) => {
        const list = find(CATEGORY, { id: value.id })
        return {
          title: t(`report.detail.category_name.${list?.code}`),
          value: value.value,
          fill: list?.color || "#333",
          bg_fill: list?.bg_color || "#CCC",
        }
      })
    }
    arc(document.getElementById('svg_category_past'), 200, 150, 120, pastList, "past", focusTitle)
    outerLabel(document.getElementById('label_category_past'), pastList, "past", focusTitle)
    arc(document.getElementById('svg_category_current'), 200, 200, 150, currentList, "current", focusTitle)
    outerLabel(document.getElementById('label_category_current'), currentList, "current", focusTitle)
  }, [isDisplayTime, focusTitle, reportData])

  useEffect(() => {
    if (!reportData?.category_work_comparisons) return
    setTimeMaxValue(Math.max(...reportData.category_work_comparisons.current.time.data.map((val) => val.value)))
    setBizMaxValue(Math.max(...reportData.category_work_comparisons.current.biz.data.map((val) => val.value)))
  }, [reportData])

  useEffect(() => {
    if (!reportData?.category_work_comparisons) return
    let idList = []
    let pastIdList = []
    if (isDisplayTime) {
      idList = reportData.category_work_comparisons.current.time.data.map((data) => data.id)
      pastIdList = reportData.category_work_comparisons.past.time.data.map((data) => data.id)
    } else {
      idList = reportData.category_work_comparisons.current.biz.data.map((data) => data.id)
      pastIdList = reportData.category_work_comparisons.past.biz.data.map((data) => data.id)
    }

    const result = idList.map((id) => {
      const category = find(CATEGORY, { id: id })
      return {
        id: category?.id || 0,
        title: t(`report.detail.category_name.${category?.code}`),
        fill: category?.color || "#333",
        bg_fill: category?.bg_color || "#CCC",
        iconPath: `/static/images/icon/icon-category-${category?.code}.svg`,
        past: {
          time: reportData.category_work_comparisons?.past.time.data.find((data) => data.id === id)?.value || 0,
          biz: reportData.category_work_comparisons?.past.biz.data.find((data) => data.id === id)?.value || 0,
        },
        current: {
          time: reportData.category_work_comparisons?.current.time.data.find((data) => data.id === id)?.value || 0,
          biz: reportData.category_work_comparisons?.current.biz.data.find((data) => data.id === id)?.value || 0,
        },
      }
    })
    setCategoryList(result)

    const tmpLabelList = Array.from(new Set([...idList, ...pastIdList])).map((id) => {
      const category = find(CATEGORY, { id: id })
      return {
        id: category?.id || 0,
        title: t(`report.detail.category_name.${category?.code}`),
        fill: category?.color || "#CCC",
      }
    })
    setLabelList(tmpLabelList)
  }, [isDisplayTime, reportData])

  useEffect(() => {
    if (!target) return
    if (target === "team") {
      if (!report.monthlyTeamReport) return
      setReportData(report.monthlyTeamReport)
    } else {
      if (!report.monthlyMemberReport) return
      setReportData(report.monthlyMemberReport)
    }
  }, [target, report])

  return (
    <>
      {
        report.isLoading ? (
          <>
            <Loading />
          </>
        ) : (
          <>
            <section id="monthly_category" className="page_contents">
              <div className="content-block monthly">
                <div className="block-header">
                  <div className="title">
                    分類別 作業比較
                  </div>
                </div>
                <div className="block-detail">
                  <div className="comparison">
                    <div className="left_area">
                      <div className="header_area">
                        <div className="text">
                          先月との比較
                        </div>
                        <div className="bar" />
                        <div className="switch_area">
                          <div
                            className={`left ${isDisplayTime ? "active" : ""}`}
                            onClick={() => switchDisplay(true)}
                            role="none"
                          >
                            時間
                          </div>
                          <div
                            className={`right ${isDisplayTime ? "" : "active"}`}
                            onClick={() => switchDisplay(false)}
                            role="none"
                          >
                            Biz
                          </div>
                        </div>
                      </div>
                      <div className="past_graph">
                        <svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="400" height="300" id="svg_category_past" />
                        <div className="display_title">
                          先月
                        </div>
                        <div id="label_category_past" />
                        <div
                          className="display_tip"
                          style={{
                            opacity: `${isDisplayPastTip ? 1 : 0}`,
                          }}
                          ref={tipPastRef}
                        />
                      </div>
                      <div className="current_graph">
                        <svg xmlns="http://www.w3.org/2000/svg" version="1.1" width="400" height="400" id="svg_category_current" />
                        <div className="display_title">
                          今月
                        </div>
                        <div id="label_category_current" />
                        <div
                          className="display_tip"
                          style={{
                            opacity: `${isDisplayCurrentTip ? 1 : 0}`,
                          }}
                          ref={tipCurrentRef}
                        />
                      </div>
                    </div>
                    <div className="right_area">
                      <div className="header_area">
                        <div className="text">
                          時間と量の比較
                        </div>
                        <div className="bar" />
                      </div>
                      {
                        categoryList.map((list) => (
                          <div
                            className="item"
                            key={`list_${list.title}`}
                          >
                            {
                              isDisplayTime
                              && reportData?.category_work_comparisons?.current.time.comment.key === list.id
                              && secToHour(Math.abs(reportData?.category_work_comparisons?.current.time.comment.fluctuation)) > 0
                              && (
                                <>
                                  <ReactSVG
                                    src="/static/images/icon/icon-warn.svg"
                                    className="icon_warn"
                                    onClick={() => setIsDisplayComment(!isDisplayComment)}
                                  />
                                  {
                                    isDisplayComment && (
                                      <div className="comment_wrap">
                                        <div className="comment">
                                          {list.title}
                                          が前月に比べて
                                          {secToHour(Math.abs(reportData?.category_work_comparisons?.current.time.comment.fluctuation))}
                                          時間
                                          {
                                            reportData?.category_work_comparisons?.current.time.comment.fluctuation > 0 ? (
                                              <span>
                                                増加
                                              </span>
                                            ) : (
                                              <span>
                                                減少
                                              </span>
                                            )
                                          }
                                          しました。
                                          <div
                                            className="close"
                                            role="none"
                                            onClick={() => setIsDisplayComment(false)}
                                          >
                                            ×
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  }
                                </>
                              )
                            }
                            {
                              !isDisplayTime
                              && reportData?.category_work_comparisons?.current.biz.comment.key === list.id
                              && secToHour(Math.abs(reportData?.category_work_comparisons?.current.biz.comment.fluctuation)) > 0
                              && (
                                <>
                                  <ReactSVG
                                    src="/static/images/icon/icon-warn.svg"
                                    className="icon_warn"
                                    onClick={() => setIsDisplayComment(!isDisplayComment)}
                                  />
                                  {
                                    isDisplayComment && (
                                      <div className="comment_wrap">
                                        <div className="comment">
                                          {list.title}
                                          が前月に比べて
                                          {Math.abs(reportData?.category_work_comparisons?.current.biz.comment.fluctuation)}
                                          Biz
                                          {
                                            reportData?.category_work_comparisons?.current.biz.comment.fluctuation > 0 ? (
                                              <span>
                                                増加
                                              </span>
                                            ) : (
                                              <span>
                                                減少
                                              </span>
                                            )
                                          }
                                          しました。
                                          <div
                                            className="close"
                                            role="none"
                                            onClick={() => setIsDisplayComment(false)}
                                          >
                                            ×
                                          </div>
                                        </div>
                                      </div>
                                    )
                                  }
                                </>
                              )
                            }
                            <div
                              className="icon"
                              style={{
                                backgroundColor: list.bg_fill || "#CCC",
                                opacity: !focusTitle || focusTitle === list.title ? 1 : 0.3,
                              }}
                              onClick={() => clickArcArea(list.title)}
                              role="none"
                            >
                              <ReactSVG
                                src={list.iconPath}
                              />
                            </div>
                            <div className="text_area">
                              <div className="title">
                                {list.title}
                              </div>
                              <div className="button_area">
                                {
                                  reportData && "team" in reportData && (
                                    <button
                                      data-gtm-click="category - detail - button"
                                      type="button"
                                      className="link_button"
                                      onClick={() => searchModalOpenCallback(
                                        {
                                          startDate: reportData?.date?.from || nowDate('YYYY-MM-DD'),
                                          endDate: reportData?.date?.to || nowDate('YYYY-MM-DD'),
                                          teamIds: [reportData?.team?.id || 0],
                                          teamNames: [reportData?.team?.name || ""],
                                          memberIds: [],
                                          memberNames: [],
                                          keywords: [],
                                          serviceNames: [],
                                          categoryIds: [list.id],
                                          categoryNames: [list.title],
                                          efficiencyLevel: [],
                                          stressLevel: [],
                                          focusLevel: [],
                                          sortKey: isDisplayTime ? "time" : "biz",
                                          sortType: "desc",
                                        },
                                      )}
                                    >
                                      詳細を見る
                                    </button>
                                  )
                                }
                                {
                                  reportData && "member" in reportData && (
                                    <button
                                      data-gtm-click="category - detail - button"
                                      type="button"
                                      className="link_button"
                                      onClick={() => searchModalOpenCallback(
                                        {
                                          startDate: reportData?.date?.from || nowDate('YYYY-MM-DD'),
                                          endDate: reportData?.date?.to || nowDate('YYYY-MM-DD'),
                                          teamIds: [],
                                          teamNames: [],
                                          memberIds: [reportData?.member?.id || 0],
                                          memberNames: [reportData?.member?.name || ""],
                                          keywords: [],
                                          serviceNames: [],
                                          categoryIds: [list.id],
                                          categoryNames: [list.title],
                                          efficiencyLevel: [],
                                          stressLevel: [],
                                          focusLevel: [],
                                          sortKey: isDisplayTime ? "time" : "biz",
                                          sortType: "desc",
                                        },
                                      )}
                                    >
                                      詳細を見る
                                    </button>
                                  )
                                }
                              </div>
                            </div>
                            <div
                              className="bar_graph_wrap"
                              style={{
                                opacity: !focusTitle || focusTitle === list.title ? 1 : 0.1,
                              }}
                            >
                              <div className="bar_graph_item">
                                <div className="title">
                                  時間
                                </div>
                                <div className={`bar_graph_area ${isDisplayTime && "active"}`}>
                                  <div
                                    className="bar_graph"
                                    style={{
                                      width: `${(list.current.time / timeMaxValue) * 100}%`,
                                    }}
                                  >
                                    <div className="bar_graph_text">
                                      {Math.floor(list.current.time / 3600)}
                                      :
                                      {`00${Math.floor((list.current.time % 3600) / 60)}`.slice(-2)}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="bar_graph_item">
                                <div className="title">
                                  Biz
                                </div>
                                <div className={`bar_graph_area ${!isDisplayTime && "active"}`}>
                                  <div
                                    className="bar_graph"
                                    style={{
                                      width: `${(list.current.biz / bizMaxValue) * 100}%`,
                                    }}
                                  >
                                    <div className="bar_graph_text">
                                      {list.current.biz.toLocaleString()}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                      }
                    </div>
                  </div>
                  <div className="label_area">
                    {
                      labelList.map((list) => (
                        <div
                          className="label_item"
                          style={{
                            opacity: !focusTitle || focusTitle === list.title ? 1 : 0.3,
                          }}
                          onClick={() => clickArcArea(list.title)}
                          role="none"
                          key={`label_${list.title}`}
                        >
                          <div
                            className="label_color"
                            style={{
                              backgroundColor: list.fill || "#CCC",
                            }}
                          />
                          <div className="label_text">
                            {list.title}
                          </div>
                        </div>
                      ))
                    }
                  </div>
                </div>
              </div>
            </section>
            <div className={`close_screen ${focusTitle || 'none'}`} role="presentation" onClick={() => closeScreen()} />
          </>
        )
      }
    </>
  )
}

export default MonthlyCategory
